/**
 * cart-drawer-functionality.js
 * A simple, robust cart drawer implementation for Shopify stores
 */

class ShopifyCartDrawer {
  constructor(options = {}) {
    // Default settings
    this.settings = {
      cartDrawerId: 'cart-drawer',
      cartDrawerContentId: 'cart-drawer-content',
      cartDrawerCloseClass: 'cart-drawer-close',
      cartDrawerOverlayId: 'cart-drawer-overlay',
      cartTriggerClass: 'cta-btn-cart',
      addToCartForms: 'form[action*="/cart/add"]',
      cartErrorClass: 'cart-error',
      cartUpdateTimeout: 300,
      moneySymbol: '$',
      // PLACEHOLDER discount icon - swap for your own sprite URL.
      discountIconUrl: "https://cdn.shopify.com/s/files/1/0728/5735/4473/files/cart-sprite-discount-tag.svg",
      ...options
    };

    // Cart state
    this.cart = {
      items: [],
      item_count: 0,
      total_price: 0,
      isUpdating: false
    };

    // Initialize elements
    this.drawer = null;
    this.drawerContent = null;
    this.overlay = null;

    // Initialize
    this.init();
  }

  init() {
    // Create drawer elements if they don't exist
    this.createDrawerElements();

    // Setup event listeners
    this.setupEventListeners();

    // Process URL params for direct linking
    this.processUrlParams();

    // Get initial cart data
    this.fetchCartData();
  }

  createDrawerElements() {
    // Check if drawer already exists
    if (!document.getElementById(this.settings.cartDrawerId)) {
      // Create drawer elements
      this.drawer = document.createElement('div');
      this.drawer.id = this.settings.cartDrawerId;
      this.drawer.className = 'cart-drawer';
      this.drawer.setAttribute('aria-hidden', 'true');

      this.drawerContent = document.createElement('div');
      this.drawerContent.id = this.settings.cartDrawerContentId;
      this.drawerContent.className = 'cart-drawer-content';

      this.overlay = document.createElement('div');
      this.overlay.id = this.settings.cartDrawerOverlayId;
      this.overlay.className = 'cart-drawer-overlay';

      // Create close button
      const closeButton = document.createElement('button');
      closeButton.className = this.settings.cartDrawerCloseClass;
      closeButton.setAttribute('aria-label', 'Close cart');
      closeButton.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      // Add loading spinner container
      const loadingSpinner = document.createElement('div');
      loadingSpinner.className = 'cart-drawer-loading';
      loadingSpinner.innerHTML = `
        <div class="spinner">
          <div class="bounce1"></div>
          <div class="bounce2"></div>
          <div class="bounce3"></div>
        </div>
      `;

      // Assemble drawer
      this.drawerContent.appendChild(closeButton);
      this.drawerContent.appendChild(loadingSpinner);
      this.drawer.appendChild(this.drawerContent);

      // Add to DOM
      document.body.appendChild(this.overlay);
      document.body.appendChild(this.drawer);

      // Add initial styles
      this.addInitialStyles();
    } else {
      // Get existing elements
      this.drawer = document.getElementById(this.settings.cartDrawerId);
      this.drawerContent = document.getElementById(this.settings.cartDrawerContentId);
      this.overlay = document.getElementById(this.settings.cartDrawerOverlayId);
    }
  }

  addInitialStyles() {
    // Check if styles are already loaded via CSS file
    if (!document.getElementById('cart-drawer-styles-check')) {
      // Create a small marker element to indicate we've checked for styles
      const styleCheckElement = document.createElement('div');
      styleCheckElement.id = 'cart-drawer-styles-check';
      styleCheckElement.style.display = 'none';
      document.body.appendChild(styleCheckElement);
      
      // Check if the CSS file is already loaded
      const cartDrawerStylesheet = document.querySelector('link[href*="cart-drawer.css"]');
      
      if (!cartDrawerStylesheet) {
        console.warn('Cart drawer CSS not loaded. Please make sure to include cart-drawer.css in your theme.');
      }
    }
  }

  setupEventListeners() {
    // Global event listeners
    document.addEventListener('click', this.handleDocumentClick.bind(this));
    
    // Close button and overlay
    const closeButtons = document.querySelectorAll(`.${this.settings.cartDrawerCloseClass}`);
    closeButtons.forEach(button => {
      button.addEventListener('click', this.closeDrawer.bind(this));
    });
    
    this.overlay.addEventListener('click', this.closeDrawer.bind(this));
    
    // Add to cart forms
    document.querySelectorAll(this.settings.addToCartForms).forEach(form => {
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    });
    
    // Cart triggers
    document.querySelectorAll(`.${this.settings.cartTriggerClass}`).forEach(trigger => {
      trigger.addEventListener('click', this.handleCartTriggerClick.bind(this));
    });
    
    // Listen for cart updates
    document.addEventListener('cart:refresh', this.fetchCartData.bind(this));
    document.addEventListener('cart:build', this.fetchCartData.bind(this));
    
    // Handle direct adding to cart via URL links
    this.handleDirectLinks();
    
    // Add a mutation observer to watch for dynamically added close buttons
    this.setupMutationObserver();
  }
  
  setupMutationObserver() {
    // Create a mutation observer to watch for changes to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          // Check for newly added close buttons
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // ELEMENT_NODE
              const closeButtons = node.querySelectorAll ? 
                node.querySelectorAll(`.${this.settings.cartDrawerCloseClass}`) : [];
              
              if (node.classList && node.classList.contains(this.settings.cartDrawerCloseClass)) {
                // The node itself is a close button
                node.addEventListener('click', this.closeDrawer.bind(this));
              }
              
              // Add event listeners to any close buttons found
              closeButtons.forEach(button => {
                button.addEventListener('click', this.closeDrawer.bind(this));
              });
            }
          });
        }
      });
    });
    
    // Start observing the document with the configured parameters
    observer.observe(document.body, { childList: true, subtree: true });
  }

  handleDocumentClick(event) {
    // Check if this is a cart trigger
    const cartTriggers = [
      '.cart-icon-bubble', 
      '.site-header__cart', 
      '.cart-link', 
      '[data-drawer-trigger="cart"]', 
      '.js-drawer-open-right'
    ].join(',');
    
    if (event.target.matches(cartTriggers) || event.target.closest(cartTriggers)) {
      event.preventDefault();
      this.openDrawer();
      return;
    }
  }

  handleDirectLinks() {
    // Find all links that add to cart
    document.querySelectorAll('a[href*="/cart/add"]').forEach(link => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const url = new URL(link.href);
        const params = new URLSearchParams(url.search);
        
        // Create form data from URL params
        const formData = new FormData();
        for (const [key, value] of params.entries()) {
          formData.append(key, value);
        }
        
        // Add to cart
        this.addToCart(formData);
      });
    });
  }

  handleCartTriggerClick(event) {
    event.preventDefault();
    this.openDrawer();
  }

  handleFormSubmit(event) {
    event.preventDefault();
    const form = event.target;
    
    // Add fusion cart property if it doesn't exist
    if (!form.querySelector('input[name="properties[_fusion_cart]"]')) {
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = 'properties[_fusion_cart]';
      hiddenInput.value = 'true';
      form.appendChild(hiddenInput);
    }
    
    const formData = new FormData(form);
    this.addToCart(formData);
  }

  processUrlParams() {
    // Check if URL has cart add parameters
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    
    if (params.has('id') && url.pathname.includes('/cart/add')) {
      const formData = new FormData();
      for (const [key, value] of params.entries()) {
        formData.append(key, value);
      }
      
      // Add fusion cart property
      formData.append('properties[_fusion_cart]', 'true');
      
      // Add to cart with slight delay to ensure page is loaded
      setTimeout(() => {
        this.addToCart(formData);
        
        // Clean URL after processing
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }, 500);
    }
  }

  addToCart(formData) {
    this.setLoading(true);
    
    // Convert FormData to JSON
    const data = {};
    formData.forEach((value, key) => {
      // Handle properties special case
      if (key.includes('properties[')) {
        if (!data.properties) data.properties = {};
        const propName = key.match(/properties\[(.*?)\]/)[1];
        data.properties[propName] = value;
      } else {
        data[key] = value;
      }
    });
    
    // Add to cart via AJAX
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(error => {
          throw new Error(error.description || 'Error adding item to cart');
        });
      }
      return response.json();
    })
    .then(item => {
      // Show success notification
      this.showItemAddedNotification();
      
      // Refresh cart data and open drawer
      return this.fetchCartData().then(() => {
        this.openDrawer();
      });
    })
    .catch(error => {
      this.showError(error.message);
    })
    .finally(() => {
      this.setLoading(false);
    });
  }

  showItemAddedNotification() {
    // Create or get notification element
    let notification = document.querySelector('.cart-drawer-item-added');
    if (!notification) {
      notification = document.createElement('div');
      notification.className = 'cart-drawer-item-added';
      notification.textContent = 'Item added to cart';
      document.body.appendChild(notification);
    }
    
    // Show notification
    notification.classList.add('show');
    
    // Hide after 3 seconds
    setTimeout(() => {
      notification.classList.remove('show');
      
      // Remove element from DOM after fadeout animation completes
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300); // match the CSS transition duration
    }, 3000);
  }

  showError(message) {
    // Find or create error element
    let errorElement = this.drawerContent.querySelector(`.${this.settings.cartErrorClass}`);
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = this.settings.cartErrorClass;
      this.drawerContent.insertBefore(errorElement, this.drawerContent.firstChild);
    }
    
    // Set error message and show
    errorElement.textContent = message;
    errorElement.classList.add('active');
    
    // Hide after 5 seconds
    setTimeout(() => {
      errorElement.classList.remove('active');
    }, 5000);
  }

  fetchCartData() {
  if (this.cart.isUpdating) return Promise.resolve();

  this.cart.isUpdating = true;
  this.setLoading(true);

  return fetch('/cart.json')
    .then(response => {
      if (!response.ok) {
        throw new Error(`/cart.json responded with status ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Expected JSON from /cart.json but got: ${contentType}`);
      }
      return response.json();
    })
    .then(cart => {
      this.cart = { ...cart, isUpdating: false };
      return this.fetchCartHtml();
    })
    .catch(error => {
      console.error('Error fetching cart:', error);
      this.cart.isUpdating = false;
      this.setLoading(false);
    });
}

  fetchCartHtml() {
    return fetch('/?section_id=cart-drawer')
      .then(response => response.text())
      .then(html => {
        // Parse the HTML and extract just the cart drawer content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        const drawerContent = tempDiv.querySelector('#cart-drawer-content');
        
        if (drawerContent) {
          // Replace our content with the fresh HTML
          this.updateDrawerContent(drawerContent.innerHTML);
        } else {
          // No cart-drawer section found, build our own HTML
          this.buildCartHtml();
        }
        
        this.setupCartItemEvents();
        this.setLoading(false);
        return this.cart;
      })
      .catch(error => {
        console.error('Error fetching cart HTML:', error);
        // Fallback to building our own cart HTML
        this.buildCartHtml();
        this.setupCartItemEvents();
        this.setLoading(false);
      });
  }

  /* ---------------------------------------------------------------------
   * Pricing helpers
   * These mirror the logic in sections/cart-drawer.liquid so the JS
   * fallback renderer and the Liquid section never disagree.
   * ------------------------------------------------------------------ */

  /**
   * One-time-purchase (OTP) price per unit, in cents.
   * For a subscription line, selling_plan_allocation.compare_at_price is the
   * variant's regular price with no selling plan applied - that's our anchor.
   * For a normal line, original_price is the price before line-level discounts.
   * compare_at_price (MSRP) is deliberately never used here.
   */
  getOtpUnitPrice(item) {
    const allocation = item.selling_plan_allocation;
    if (allocation && allocation.compare_at_price > 0) {
      return allocation.compare_at_price;
    }
    return item.original_price || item.price || 0;
  }

  /**
   * What the customer actually pays per unit, after the selling plan
   * adjustment and any line-level discount (code or automatic).
   */
  getPaidUnitPrice(item) {
    if (typeof item.final_price === 'number') return item.final_price;
    if (typeof item.discounted_price === 'number') return item.discounted_price;
    return item.price || 0;
  }

  /**
   * Cart-only compare-at override, in cents.
   *
   * /cart.json does NOT expose variant metafields, so this reads from an
   * optional line item property instead. If you want the fallback renderer to
   * honour the override, add `properties[_compare_at_override]` (in dollars,
   * e.g. "66.65") at add-to-cart. Otherwise this returns 0 and the fallback
   * uses the OTP anchor - the Liquid section is the source of truth.
   */
  getOverridePrice(item) {
    const raw =
      (item.properties && (item.properties._compare_at_override || item.properties.compare_at_override))
      || item.compare_at_price_override;

    if (!raw) return 0;

    const parsed = parseFloat(String(raw).replace(/[$,\s]/g, ''));
    if (!isFinite(parsed) || parsed <= 0) return 0;

    return Math.round(parsed * 100);
  }

  /**
   * Does this line qualify for a savings pill?
   * Only a subscription or a real line-level discount earns one. A compare-at
   * price alone never does - including an override.
   */
  shouldShowSavings(item) {
    const hasLineDiscount = (item.line_level_total_discount || item.total_discount || 0) > 0;
    return !!item.selling_plan_allocation || hasLineDiscount;
  }

  /**
   * Whole-number savings percentage. Anchored to the compare-at override when
   * one is set, otherwise to the OTP price.
   * Swap Math.round for Math.floor if you'd rather never round up.
   */
  getSavingsPercent(item) {
    if (!this.shouldShowSavings(item)) return 0;

    const anchor = this.getOverridePrice(item) || this.getOtpUnitPrice(item);
    const paid = this.getPaidUnitPrice(item);
    if (!anchor || anchor <= paid) return 0;

    return Math.round(((anchor - paid) / anchor) * 100);
  }

  /**
   * Strike-through anchor per unit, in cents.
   *   Override set            -> override price
   *   Subscription            -> OTP price
   *   Subscription + discount -> OTP price
   *   OTP + discount          -> OTP price
   *   OTP, no discount        -> compare-at price
   *
   * Note: /cart.json exposes neither compare_at_price nor metafields on line
   * items, so the first and last cases only resolve if you've supplied them.
   * The Liquid section handles both properly; this is the fallback path.
   */
  getStrikePrice(item) {
    const override = this.getOverridePrice(item);
    if (override > 0) return override;

    if (this.shouldShowSavings(item)) {
      return this.getOtpUnitPrice(item);
    }

    return (item.variant && item.variant.compare_at_price) || item.compare_at_price || 0;
  }

  /**
   * Line-level discounts (code or automatic), normalised to { title, amount }.
   */
  getLineDiscounts(item) {
    const allocations =
      (item.line_level_discount_allocations && item.line_level_discount_allocations.length)
        ? item.line_level_discount_allocations
        : (item.discounts || []);

    return allocations
      .map(allocation => ({
        title: (allocation.discount_application && allocation.discount_application.title)
          || allocation.title
          || 'Discount',
        amount: allocation.amount || 0
      }))
      .filter(discount => discount.amount > 0);
  }

  formatMoney(cents) {
    return `${this.settings.moneySymbol}${((cents || 0) / 100).toFixed(2)}`;
  }

  escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Builds the savings % / discount code / delivery frequency tag row.
   */
  buildItemTagsHtml(item) {
    const tags = [];

    const savingsPercent = this.getSavingsPercent(item);
    if (savingsPercent > 0) {
      tags.push(
        `<span class="cart-item-tag cart-item-tag--savings cart-item-discount">Saving ${savingsPercent}%</span>`
      );
    }

    this.getLineDiscounts(item).forEach(discount => {
      tags.push(`
        <span class="cart-item-tag cart-item-tag--code">
          <img class="cart-item-tag-icon" src="${this.escapeHtml(this.settings.discountIconUrl)}" alt="" width="14" height="14" loading="lazy">
          ${this.escapeHtml(discount.title)}
        </span>
      `);
    });

    const sellingPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (sellingPlan && sellingPlan.name) {
      tags.push(
        `<span class="cart-item-tag cart-item-tag--subscription cart-item-subscription">${this.escapeHtml(sellingPlan.name)}</span>`
      );
    }

    if (!tags.length) return '';
    return `<div class="cart-item-tags cart-item-selling-plan-box">${tags.join('')}</div>`;
  }

  buildCartHtml() {
    // Build cart HTML manually
    const cartItemsHtml = this.cart.items.map(item => {
      // Only show quantity if more than 1
      const quantityHtml = item.quantity > 1 ?
        `<div class="cart-item-quantity">Quantity: ${item.quantity}</div>` : '';

      const paidPrice = this.getPaidUnitPrice(item);
      const strikePrice = this.getStrikePrice(item);

      const priceHtml = `
        <div class="cart-item-price-container">
          <span class="cart-item-price">${this.formatMoney(paidPrice)}</span>
          ${strikePrice > paidPrice ? `<span class="cart-item-original-price">${this.formatMoney(strikePrice)}</span>` : ''}
        </div>
      `;

      return `
        <div class="cart-item" data-variant-id="${item.variant_id}" data-key="${item.key}">
          <div class="cart-item-image">
            <img src="${item.image || ''}" alt="${this.escapeHtml(item.title)}">
          </div>
          <div class="cart-item-details">
            <div class="cart-item-details-info">
              <div class="cart-item-title">${this.escapeHtml(item.product_title || item.title)}</div>
              ${item.variant_title ? `<div class="cart-item-variant">${this.escapeHtml(item.variant_title)}</div>` : ''}
              ${quantityHtml}
              ${priceHtml}
              ${this.buildItemTagsHtml(item)}
            </div>
            <div class="cart-item-actions">
              <button class="cart-item-remove" aria-label="Remove item">✕</button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Total savings - sums the same anchor each line strikes through
    let totalSavings = 0;
    this.cart.items.forEach(item => {
      const strikePrice = this.getStrikePrice(item);
      const paidPrice = this.getPaidUnitPrice(item);

      if (strikePrice > paidPrice) {
        totalSavings += (strikePrice - paidPrice) * item.quantity;
      }
    });
    
    const totalSavingsHtml = totalSavings > 0 ? 
      `<div class="cart-total-savings">
        <span>Total Savings</span>
        <span>${this.formatMoney(totalSavings)}</span>
      </div>` : '';

    // Create full drawer content
    const cartHtml = `
      <div class="${this.settings.cartErrorClass}"></div>
      <button class="${this.settings.cartDrawerCloseClass}" aria-label="Close cart">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      
      ${this.cart.item_count === 0 ? `
        <div class="cart-empty-message">
          <p>Your cart is empty</p>
          <button class="cart-continue-shopping">Continue shopping</button>
        </div>
      ` : `
        <h2>Your Cart (${this.cart.item_count})</h2>
        <div class="cart-items">
          ${cartItemsHtml}
        </div>
        <div class="cart-drawer-footer">
          ${totalSavingsHtml}
          <div class="cart-subtotal">
            <span>Subtotal</span>
            <span>${this.formatMoney(this.cart.total_price)}</span>
          </div>
          <button class="cart-checkout-button">Secure Checkout</button>
          <button class="cart-continue-shopping">Continue shopping</button>
        </div>
      `}
    `;

    this.updateDrawerContent(cartHtml);
  }

  updateDrawerContent(html) {
    // Keep close button and error message
    const closeButton = this.drawerContent.querySelector(`.${this.settings.cartDrawerCloseClass}`);
    const errorElement = this.drawerContent.querySelector(`.${this.settings.cartErrorClass}`);
    const loadingElement = this.drawerContent.querySelector('.cart-drawer-loading');
    
    // Update the content
    this.drawerContent.innerHTML = html;
    
    // Make sure we have the essential elements
    if (!this.drawerContent.querySelector(`.${this.settings.cartDrawerCloseClass}`) && closeButton) {
      this.drawerContent.prepend(closeButton);
    }
    
    if (!this.drawerContent.querySelector(`.${this.settings.cartErrorClass}`) && errorElement) {
      this.drawerContent.prepend(errorElement);
    }
    
    if (!this.drawerContent.querySelector('.cart-drawer-loading') && loadingElement) {
      this.drawerContent.appendChild(loadingElement);
    }
    
    // Re-add event listeners for close button
    const newCloseButtons = this.drawerContent.querySelectorAll(`.${this.settings.cartDrawerCloseClass}`);
    newCloseButtons.forEach(button => {
      button.addEventListener('click', this.closeDrawer.bind(this));
    });
  }

  setupCartItemEvents() {
    // Remove item
    this.drawerContent.querySelectorAll('.cart-item-remove').forEach(button => {
      button.addEventListener('click', this.handleRemoveItem.bind(this));
    });
    
    // Checkout button
    const checkoutButton = this.drawerContent.querySelector('.cart-checkout-button');
    if (checkoutButton) {
      checkoutButton.addEventListener('click', () => {
        window.location.href = '/checkout';
      });
    }
    
    // Continue shopping button
    const continueButton = this.drawerContent.querySelector('.cart-continue-shopping');
    if (continueButton) {
      continueButton.addEventListener('click', this.closeDrawer.bind(this));
    }
  }

  handleRemoveItem(event) {
    const button = event.target;
    const cartItem = button.closest('.cart-item');
    const key = cartItem.dataset.key;
    
    this.updateCartItem(key, 0);
  }

  updateCartItem(key, quantity) {
    // Debounce updates
    clearTimeout(this.updateTimeout);
    
    this.updateTimeout = setTimeout(() => {
      this.setLoading(true);
      
      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      })
      .then(response => response.json())
      .then(cart => {
        this.cart = { ...cart, isUpdating: false };
        return this.fetchCartHtml();
      })
      .catch(error => {
        console.error('Error updating cart:', error);
        this.showError('Error updating cart. Please try again.');
        this.setLoading(false);
      });
    }, this.settings.cartUpdateTimeout);
  }

  openDrawer() {
    // Try the theme's built-in drawer first
    try {
      if (window.theme && window.theme.headerDrawer && typeof window.theme.headerDrawer.open === 'function') {
        window.theme.headerDrawer.open('cart');
        return;
      }
    } catch (e) {
      console.warn('Could not open theme cart drawer:', e);
    }
    
    // Use our own drawer
    this.drawer.setAttribute('aria-hidden', 'false');
    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.drawer.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.drawer.classList.add('loading');
    } else {
      this.drawer.classList.remove('loading');
    }
  }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ShopifyCartDrawer;
} else {
  // Initialize on page load for standalone usage
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawer = new ShopifyCartDrawer();
  });
}